
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useAuthUser } from './superadmin/useAuthUser';
import { useSaasInstances } from './superadmin/useSaasInstances';
import { useLicenseVerification } from './superadmin/useLicenseVerification';
import { clearUserSession } from '@/utils/sessionManager';

export const useSuperAdminDashboard = () => {
  const { toast } = useToast();
  const { user, handleLogout: authUserLogout } = useAuthUser();
  const { 
    saasInstances, 
    isLoading: isLoadingInstances, 
    loadSaasInstances, 
    handleSuspendInstance, 
    handleActivateInstance, 
    getActiveInstances, 
    getSuspendedInstances 
  } = useSaasInstances();
  const { licenseInfo, isVerifyingLicense, verifyLicense } = useLicenseVerification();
  
  const [activeTab, setActiveTab] = useState("overview");
  const [isLoading, setIsLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  
  useEffect(() => {
    const loadDashboardData = async () => {
      if (!user || initialized) return;
      
      setIsLoading(true);
      try {
        // Load SaaS instances
        await loadSaasInstances();
        
        // Verify license if we have a user
        if (user?.id) {
          await verifyLicense(user.id);
        }
        
        setInitialized(true);
      } catch (error) {
        console.error('Error loading super admin dashboard data:', error);
        toast({
          title: "Error",
          description: "Failed to load dashboard data",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    loadDashboardData();
  }, [user, toast, loadSaasInstances, verifyLicense, initialized]);

  // Improved logout handler with guaranteed redirect
  const handleLogout = async () => {
    console.log("useSuperAdminDashboard: Starting enhanced logout process");
    
    try {
      // First clear all session data
      clearUserSession();
      
      // Then call the auth user logout (might be async)
      await authUserLogout();
      
      console.log("useSuperAdminDashboard: Logout successful, redirecting");
      
      // Force immediate redirect to login page
      window.location.href = '/login';
      return true;
    } catch (error) {
      console.error('Error during logout:', error);
      
      // Still clear the session
      clearUserSession();
      
      // Still redirect to ensure logout happens
      window.location.href = '/login';
      return false;
    }
  };

  const activeInstances = getActiveInstances();
  const suspendedInstances = getSuspendedInstances();

  return {
    user,
    saasInstances,
    activeInstances,
    suspendedInstances,
    isLoading: isLoading || isLoadingInstances,
    activeTab,
    setActiveTab,
    handleLogout,
    handleSuspendInstance,
    handleActivateInstance,
    licenseInfo,
    isVerifyingLicense,
    verifyLicense
  };
};
